import React from 'react';

interface ComparisonData {
  year: number;
  scope1: number;
  scope2: number;
  scope3: number;
  total: number;
}

interface ComparisonModeCardProps {
  baseYearData: ComparisonData | null;
  reportingYearData: ComparisonData | null;
  baseYear: number;
  reportingYear: number;
}

export const ComparisonModeCard: React.FC<ComparisonModeCardProps> = ({
  baseYearData,
  reportingYearData,
  baseYear,
  reportingYear
}) => {
  const formatNumber = (num: number) => {
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const getScopeDescription = (scope: number) => {
    switch (scope) {
      case 1: return "Direct emissions from owned sources";
      case 2: return "Indirect emissions from energy";
      case 3: return "Other indirect emissions";
      default: return "";
    }
  };

  const getScopeValue = (data: ComparisonData | null, scope: number) => {
    if (!data) return 0;
    switch (scope) {
      case 1: return data.scope1;
      case 2: return data.scope2;
      case 3: return data.scope3;
      default: return 0;
    }
  };

  const getVariance = (base: number, current: number) => {
    if (base === 0) return current > 0 ? 100 : 0;
    return ((current - base) / base) * 100;
  };

  const getVarianceColor = (variance: number) => {
    if (variance > 0) return "text-red-600";
    if (variance < 0) return "text-green-600";
    return "text-gray-600";
  };

  const getVarianceIcon = (variance: number) => {
    if (variance > 0) return "↗";
    if (variance < 0) return "↘";
    return "→";
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Comparison Mode: {baseYear} vs {reportingYear}
        </h3>
        <p className="text-sm text-gray-600">
          Dashboard showing side-by-side comparison between base year and reporting year
        </p>
      </div>

      {/* Comparison Content */}
      <div className="space-y-6">
        {[1, 2, 3].map((scope) => {
          const baseValue = getScopeValue(baseYearData, scope);
          const reportingValue = getScopeValue(reportingYearData, scope);
          const variance = getVariance(baseValue, reportingValue);

          return (
            <div key={scope} className="border-b border-gray-100 pb-4 last:border-b-0">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-900">Scope {scope}</h4>
                <div className="text-xs text-gray-500">
                  {getVarianceIcon(variance)} {variance.toFixed(1)}%
                </div>
              </div>
              
              <p className="text-sm text-gray-600 mb-3">
                {getScopeDescription(scope)}
              </p>

              {/* Side-by-side values */}
              <div className="grid grid-cols-2 gap-4">
                {/* Base Year */}
                <div className="bg-blue-50 rounded-lg p-3">
                  <div className="text-xs text-blue-600 font-medium mb-1">
                    Base Year ({baseYear})
                  </div>
                  <div className="text-lg font-semibold text-blue-800">
                    {formatNumber(baseValue)} kg CO2e
                  </div>
                </div>

                {/* Reporting Year */}
                <div className="bg-green-50 rounded-lg p-3">
                  <div className="text-xs text-green-600 font-medium mb-1">
                    Reporting Year ({reportingYear})
                  </div>
                  <div className="text-lg font-semibold text-green-800">
                    {formatNumber(reportingValue)} kg CO2e
                  </div>
                </div>
              </div>

              {/* Variance indicator */}
              <div className="mt-2 text-xs">
                <span className={`font-medium ${getVarianceColor(variance)}`}>
                  {variance > 0 ? 'Increase' : variance < 0 ? 'Decrease' : 'No change'}: 
                  {Math.abs(variance).toFixed(1)}%
                </span>
                <span className="text-gray-500 ml-2">
                  ({formatNumber(Math.abs(reportingValue - baseValue))} kg CO2e difference)
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Total Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-600 font-medium mb-1">
              Base Year Total ({baseYear})
            </div>
            <div className="text-xl font-bold text-gray-900">
              {formatNumber(baseYearData?.total || 0)} kg CO2e
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-600 font-medium mb-1">
              Reporting Year Total ({reportingYear})
            </div>
            <div className="text-xl font-bold text-gray-900">
              {formatNumber(reportingYearData?.total || 0)} kg CO2e
            </div>
          </div>
        </div>
        
        {baseYearData && reportingYearData && (
          <div className="mt-3 text-center">
            <span className={`text-sm font-medium ${
              getVarianceColor(getVariance(baseYearData.total, reportingYearData.total))
            }`}>
              Overall {getVariance(baseYearData.total, reportingYearData.total) > 0 ? 'Increase' : 'Decrease'}: 
              {Math.abs(getVariance(baseYearData.total, reportingYearData.total)).toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
























